// Industry segments data for SEO industry pages

export interface IndustryData {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string; // Icon component name or path
  challenges: string[];
  opportunities: string[];
  commonServices: string[];
  incomeRange: {
    low: number;
    high: number;
  };
  specificNeeds: string[];
  careerStages: {
    stage: string;
    description: string;
    financialPriorities: string[];
  }[];
  taxConsiderations: string[];
  seoMetadata: {
    title: string;
    description: string;
    keywords: string[];
  };
}

export const industries: IndustryData[] = [
  {
    id: 'medical-professionals',
    name: 'Medical Professionals',
    slug: 'medical-professionals',
    description: 'Physicians, dentists, specialists, and healthcare providers face unique financial challenges including professional corporations, high liability risks, and complex tax situations.',
    icon: 'MedicalIcon',
    challenges: [
      'High student loan debt from medical school',
      'Professional liability and malpractice risks',
      'Complex professional corporation structures',
      'Irregular income during residency and early career',
      'Time constraints limiting financial planning'
    ],
    opportunities: [
      'Professional corporation tax advantages',
      'Income splitting opportunities',
      'Pension plan alternatives',
      'Tax-efficient investment strategies',
      'Estate freeze opportunities'
    ],
    commonServices: [
      'Professional corporation setup and optimization',
      'Disability insurance for high-income earners',
      'Malpractice tail coverage planning',
      'Tax-efficient retirement strategies',
      'Estate planning with professional assets'
    ],
    incomeRange: {
      low: 150000,
      high: 800000
    },
    specificNeeds: [
      'Maximizing small business deduction',
      'Protecting against disability',
      'Managing cash flow fluctuations',
      'Planning for practice sale or succession',
      'Balancing personal and corporate investments'
    ],
    careerStages: [
      {
        stage: 'Residency/Early Career',
        description: 'Managing debt while building foundation',
        financialPriorities: ['Debt management', 'Disability insurance', 'Basic investment strategy']
      },
      {
        stage: 'Established Practice',
        description: 'Optimizing earnings and building wealth',
        financialPriorities: ['Tax optimization', 'Investment diversification', 'Family protection']
      },
      {
        stage: 'Peak Earning Years',
        description: 'Maximizing wealth accumulation',
        financialPriorities: ['Estate planning', 'Tax minimization', 'Retirement preparation']
      },
      {
        stage: 'Transition/Retirement',
        description: 'Practice succession and retirement income',
        financialPriorities: ['Practice sale', 'Income streams', 'Legacy planning']
      }
    ],
    taxConsiderations: [
      'Professional corporation integration',
      'Capital gains exemption planning',
      'Income sprinkling rules',
      'Passive investment income limits',
      'Provincial tax differences'
    ],
    seoMetadata: {
      title: 'Financial Advisor for Medical Professionals | Physician Wealth Management',
      description: 'Specialized financial planning for doctors, dentists, and healthcare professionals. Expert strategies for professional corporations, tax optimization, and wealth preservation.',
      keywords: ['medical professional financial advisor', 'physician wealth management', 'doctor financial planning', 'dentist financial advisor']
    }
  },
  {
    id: 'small-business-owners',
    name: 'Small Business Owners',
    slug: 'small-business-owners',
    description: 'Entrepreneurs and business owners need integrated strategies that protect both personal and business assets while optimizing for growth and tax efficiency.',
    icon: 'BusinessIcon',
    challenges: [
      'Irregular cash flow and income',
      'Personal guarantees on business debt',
      'Balancing reinvestment vs. personal wealth',
      'Key person dependencies',
      'Succession planning complexity'
    ],
    opportunities: [
      'Small business tax deduction',
      'Income splitting with family',
      'Capital gains exemption',
      'Corporate investment strategies',
      'Buy-sell agreement funding'
    ],
    commonServices: [
      'Business succession planning',
      'Key person insurance',
      'Buy-sell agreements',
      'Corporate tax optimization',
      'Personal asset protection'
    ],
    incomeRange: {
      low: 75000,
      high: 500000
    },
    specificNeeds: [
      'Cash flow management',
      'Business risk mitigation',
      'Exit strategy planning',
      'Employee retention strategies',
      'Cross-border considerations'
    ],
    careerStages: [
      {
        stage: 'Startup Phase',
        description: 'Building the business foundation',
        financialPriorities: ['Cash flow management', 'Basic protection', 'Tax structure setup']
      },
      {
        stage: 'Growth Phase',
        description: 'Scaling operations and revenue',
        financialPriorities: ['Reinvestment strategies', 'Key person protection', 'Employee benefits']
      },
      {
        stage: 'Maturity Phase',
        description: 'Optimizing profitability',
        financialPriorities: ['Wealth extraction', 'Tax minimization', 'Succession planning']
      },
      {
        stage: 'Exit Phase',
        description: 'Maximizing sale value',
        financialPriorities: ['Business valuation', 'Tax-efficient sale', 'Retirement income']
      }
    ],
    taxConsiderations: [
      'Corporate vs. personal tax rates',
      'Dividend vs. salary optimization',
      'HST/GST considerations',
      'Scientific research credits',
      'Manufacturing tax incentives'
    ],
    seoMetadata: {
      title: 'Small Business Financial Advisor | Entrepreneur Wealth Management',
      description: 'Expert financial planning for Canadian small business owners. Business succession, tax strategies, and integrated wealth management for entrepreneurs.',
      keywords: ['small business financial advisor', 'entrepreneur financial planning', 'business owner wealth management', 'business succession planning']
    }
  },
  {
    id: 'corporate-executives',
    name: 'Corporate Executives',
    slug: 'corporate-executives',
    description: 'C-suite executives and senior managers require sophisticated strategies for stock options, deferred compensation, and concentrated wealth positions.',
    icon: 'ExecutiveIcon',
    challenges: [
      'Complex compensation packages',
      'Stock option timing and taxation',
      'Concentrated equity positions',
      'Golden handcuff retention plans',
      'Public company trading restrictions'
    ],
    opportunities: [
      'Stock option optimization',
      'Deferred compensation strategies',
      'Executive benefit maximization',
      'Tax-loss harvesting',
      'Alternative minimum tax planning'
    ],
    commonServices: [
      'Stock option planning',
      'Concentrated position management',
      'Executive benefit optimization',
      'Tax-efficient diversification',
      'Retirement transition planning'
    ],
    incomeRange: {
      low: 200000,
      high: 2000000
    },
    specificNeeds: [
      'Equity compensation strategies',
      'Risk management for concentrated positions',
      'Tax bracket management',
      'Estate liquidity planning',
      'Charitable giving strategies'
    ],
    careerStages: [
      {
        stage: 'Rising Executive',
        description: 'Building executive presence',
        financialPriorities: ['Career advancement', 'Equity accumulation', 'Tax planning']
      },
      {
        stage: 'Senior Leadership',
        description: 'Peak earning and influence',
        financialPriorities: ['Wealth diversification', 'Estate planning', 'Tax optimization']
      },
      {
        stage: 'Pre-Retirement',
        description: 'Transition planning',
        financialPriorities: ['Retirement readiness', 'Succession planning', 'Legacy preparation']
      },
      {
        stage: 'Post-Executive',
        description: 'Board positions and consulting',
        financialPriorities: ['Income replacement', 'Philanthropy', 'Family wealth transfer']
      }
    ],
    taxConsiderations: [
      'Stock option deduction limits',
      'Alternative minimum tax',
      'Departure tax planning',
      'Trust and estate strategies',
      'Cross-border taxation'
    ],
    seoMetadata: {
      title: 'Executive Financial Advisor | C-Suite Wealth Management Services',
      description: 'Specialized financial planning for corporate executives. Expert strategies for stock options, executive compensation, and sophisticated wealth preservation.',
      keywords: ['executive financial advisor', 'C-suite wealth management', 'stock option planning', 'executive compensation advisor']
    }
  },
  {
    id: 'retirees',
    name: 'Retirees & Pre-Retirees',
    slug: 'retirees',
    description: 'Canadians approaching or in retirement need strategies to maximize income, minimize taxes, and ensure their wealth lasts throughout retirement.',
    icon: 'RetirementIcon',
    challenges: [
      'Longevity risk and outliving assets',
      'Healthcare and long-term care costs',
      'Inflation impact on fixed income',
      'Estate tax minimization',
      'Family wealth transfer complexity'
    ],
    opportunities: [
      'Income splitting strategies',
      'Tax-efficient withdrawal planning',
      'Estate freeze techniques',
      'Charitable giving benefits',
      'Inter-generational wealth transfer'
    ],
    commonServices: [
      'Retirement income planning',
      'Tax-efficient withdrawal strategies',
      'Estate planning and wills',
      'Long-term care insurance',
      'Legacy and charitable planning'
    ],
    incomeRange: {
      low: 60000,
      high: 250000
    },
    specificNeeds: [
      'Sustainable withdrawal rates',
      'Government benefit optimization',
      'Healthcare cost planning',
      'Estate liquidity',
      'Family cottage succession'
    ],
    careerStages: [
      {
        stage: 'Pre-Retirement (55-65)',
        description: 'Final accumulation phase',
        financialPriorities: ['Retirement readiness', 'Debt elimination', 'Tax planning']
      },
      {
        stage: 'Early Retirement (65-75)',
        description: 'Active retirement years',
        financialPriorities: ['Income optimization', 'Travel and lifestyle', 'Health planning']
      },
      {
        stage: 'Mid Retirement (75-85)',
        description: 'Wealth preservation focus',
        financialPriorities: ['Capital preservation', 'Healthcare costs', 'Estate planning']
      },
      {
        stage: 'Late Retirement (85+)',
        description: 'Legacy and care planning',
        financialPriorities: ['Long-term care', 'Wealth transfer', 'Final arrangements']
      }
    ],
    taxConsiderations: [
      'RRIF minimum withdrawals',
      'OAS clawback planning',
      'Pension income splitting',
      'Tax-free savings account strategies',
      'Estate tax minimization'
    ],
    seoMetadata: {
      title: 'Retirement Financial Advisor | Canadian Retirement Planning Expert',
      description: 'Expert retirement planning for Canadians. Maximize retirement income, minimize taxes, and create lasting legacies with comprehensive wealth strategies.',
      keywords: ['retirement financial advisor', 'retirement planning Canada', 'retirement income strategies', 'pre-retirement planning']
    }
  },
  {
    id: 'high-net-worth-families',
    name: 'High-Net-Worth Families',
    slug: 'high-net-worth-families',
    description: 'Affluent families require sophisticated multi-generational strategies for wealth preservation, tax optimization, and family governance.',
    icon: 'FamilyIcon',
    challenges: [
      'Multi-generational wealth transfer',
      'Family governance and communication',
      'Complex tax situations',
      'Asset protection concerns',
      'Philanthropic planning'
    ],
    opportunities: [
      'Family trust structures',
      'Inter-generational wealth transfer',
      'Private foundation creation',
      'Tax-efficient investment strategies',
      'Family office services'
    ],
    commonServices: [
      'Family office advisory',
      'Trust and estate planning',
      'Tax optimization strategies',
      'Philanthropic planning',
      'Next generation education'
    ],
    incomeRange: {
      low: 250000,
      high: 5000000
    },
    specificNeeds: [
      'Wealth preservation across generations',
      'Family governance structures',
      'Privacy and asset protection',
      'Coordinated advisory team',
      'Values-based wealth transfer'
    ],
    careerStages: [
      {
        stage: 'Wealth Creation',
        description: 'First generation building wealth',
        financialPriorities: ['Asset accumulation', 'Tax efficiency', 'Risk management']
      },
      {
        stage: 'Wealth Preservation',
        description: 'Protecting and growing wealth',
        financialPriorities: ['Asset protection', 'Estate planning', 'Family education']
      },
      {
        stage: 'Wealth Transfer',
        description: 'Passing wealth to next generation',
        financialPriorities: ['Trust structures', 'Tax minimization', 'Family governance']
      },
      {
        stage: 'Multi-Generational',
        description: 'Legacy and philanthropy',
        financialPriorities: ['Perpetual wealth', 'Charitable impact', 'Family unity']
      }
    ],
    taxConsiderations: [
      'Trust taxation strategies',
      'Estate freeze techniques',
      'Cross-border tax planning',
      'Alternative minimum tax',
      'Charitable tax credits'
    ],
    seoMetadata: {
      title: 'High-Net-Worth Family Financial Advisor | Wealth Preservation Expert',
      description: 'Elite wealth management for affluent Canadian families. Multi-generational planning, tax optimization, and sophisticated strategies for substantial wealth.',
      keywords: ['high net worth financial advisor', 'family wealth management', 'wealth preservation strategies', 'multi-generational planning']
    }
  },
  {
    id: 'tech-professionals',
    name: 'Technology Professionals',
    slug: 'tech-professionals',
    description: 'Tech workers and startup employees need specialized strategies for equity compensation, volatile income, and rapid wealth accumulation.',
    icon: 'TechIcon',
    challenges: [
      'Stock option complexity',
      'Startup equity valuation',
      'Income volatility',
      'Cross-border taxation',
      'Concentrated tech positions'
    ],
    opportunities: [
      'Equity compensation optimization',
      'Tax-deferred exchanges',
      'Angel investing strategies',
      'Startup retirement plans',
      'Capital gains planning'
    ],
    commonServices: [
      'Stock option advisory',
      'Equity compensation planning',
      'Startup financial planning',
      'Cross-border tax strategies',
      'Concentrated position management'
    ],
    incomeRange: {
      low: 100000,
      high: 500000
    },
    specificNeeds: [
      'ISO vs NSO optimization',
      'Exercise timing strategies',
      '83(b) election planning',
      'Liquidity event preparation',
      'Risk diversification'
    ],
    careerStages: [
      {
        stage: 'Early Career',
        description: 'Building technical skills',
        financialPriorities: ['Emergency fund', 'Stock option understanding', 'Basic investing']
      },
      {
        stage: 'Senior Developer',
        description: 'Maximizing compensation',
        financialPriorities: ['Equity optimization', 'Tax planning', 'Property purchase']
      },
      {
        stage: 'Tech Leadership',
        description: 'Executive or founder role',
        financialPriorities: ['Liquidity planning', 'Wealth diversification', 'Estate planning']
      },
      {
        stage: 'Post-Exit',
        description: 'After liquidity event',
        financialPriorities: ['Wealth preservation', 'Angel investing', 'Philanthropy']
      }
    ],
    taxConsiderations: [
      'Stock option taxation',
      'Capital gains optimization',
      'Alternative minimum tax',
      'Cross-border considerations',
      'Qualified small business shares'
    ],
    seoMetadata: {
      title: 'Tech Professional Financial Advisor | Startup Equity Planning',
      description: 'Specialized financial planning for tech professionals. Expert guidance on stock options, equity compensation, and wealth strategies for the innovation economy.',
      keywords: ['tech financial advisor', 'startup equity planning', 'stock option advisor', 'tech professional wealth management']
    }
  },
  {
    id: 'legal-professionals',
    name: 'Legal Professionals',
    slug: 'legal-professionals',
    description: 'Lawyers and legal professionals face unique challenges with partnership structures, professional liability, and irregular income patterns.',
    icon: 'LegalIcon',
    challenges: [
      'Partnership buy-in costs',
      'Professional liability exposure',
      'Irregular billing cycles',
      'Work-life balance impact',
      'Partnership succession'
    ],
    opportunities: [
      'Professional corporation benefits',
      'Partnership tax planning',
      'Income averaging strategies',
      'Retirement compensation arrangements',
      'Associate to partner transition'
    ],
    commonServices: [
      'Partnership financial planning',
      'Professional liability coverage',
      'Tax-efficient structures',
      'Retirement planning for partners',
      'Succession planning'
    ],
    incomeRange: {
      low: 80000,
      high: 1000000
    },
    specificNeeds: [
      'Partnership agreement funding',
      'Professional corporation setup',
      'Income smoothing strategies',
      'Disability protection',
      'Cross-border practice issues'
    ],
    careerStages: [
      {
        stage: 'Associate',
        description: 'Building legal career',
        financialPriorities: ['Debt management', 'Basic protection', 'Tax planning']
      },
      {
        stage: 'Senior Associate',
        description: 'Partnership track',
        financialPriorities: ['Partnership preparation', 'Income growth', 'Investment planning']
      },
      {
        stage: 'Partner',
        description: 'Equity ownership',
        financialPriorities: ['Tax optimization', 'Wealth accumulation', 'Risk management']
      },
      {
        stage: 'Senior Partner',
        description: 'Legacy building',
        financialPriorities: ['Succession planning', 'Estate strategies', 'Retirement transition']
      }
    ],
    taxConsiderations: [
      'Partnership taxation',
      'Professional corporation integration',
      'Trust account considerations',
      'GST/HST on legal services',
      'International tax treaties'
    ],
    seoMetadata: {
      title: 'Legal Professional Financial Advisor | Law Firm Partner Planning',
      description: 'Expert financial planning for lawyers and legal professionals. Partnership planning, tax strategies, and wealth management for the legal community.',
      keywords: ['lawyer financial advisor', 'legal professional wealth management', 'law partner financial planning', 'attorney financial advisor']
    }
  },
  {
    id: 'real-estate-professionals',
    name: 'Real Estate Professionals',
    slug: 'real-estate-professionals',
    description: 'Real estate agents, brokers, and developers need strategies for variable income, market cycles, and investment property portfolios.',
    icon: 'RealEstateIcon',
    challenges: [
      'Commission-based income volatility',
      'Market cycle dependence',
      'Self-employment tax burden',
      'Investment property management',
      'Retirement savings discipline'
    ],
    opportunities: [
      'Real estate investment strategies',
      'Tax deduction optimization',
      'Incorporation benefits',
      'RRSP contribution planning',
      'Capital gains strategies'
    ],
    commonServices: [
      'Income smoothing strategies',
      'Investment property planning',
      'Tax optimization for agents',
      'Retirement planning without pension',
      'Market downturn protection'
    ],
    incomeRange: {
      low: 40000,
      high: 500000
    },
    specificNeeds: [
      'Cash flow management',
      'Tax installment planning',
      'Investment diversification',
      'Business expense optimization',
      'Market risk mitigation'
    ],
    careerStages: [
      {
        stage: 'New Agent',
        description: 'Building client base',
        financialPriorities: ['Cash reserves', 'Business setup', 'Basic protection']
      },
      {
        stage: 'Established Agent',
        description: 'Growing reputation',
        financialPriorities: ['Tax planning', 'Investment strategy', 'Income stability']
      },
      {
        stage: 'Top Producer',
        description: 'Market leader',
        financialPriorities: ['Wealth diversification', 'Team building', 'Tax optimization']
      },
      {
        stage: 'Broker/Owner',
        description: 'Business ownership',
        financialPriorities: ['Business value', 'Succession planning', 'Legacy building']
      }
    ],
    taxConsiderations: [
      'Commission income planning',
      'HST registration requirements',
      'Home office deductions',
      'Vehicle expense claims',
      'Investment property taxation'
    ],
    seoMetadata: {
      title: 'Real Estate Professional Financial Advisor | Realtor Wealth Planning',
      description: 'Specialized financial planning for real estate professionals. Income strategies, tax planning, and investment management for agents and brokers.',
      keywords: ['real estate financial advisor', 'realtor financial planning', 'real estate agent wealth management', 'broker financial advisor']
    }
  },
  {
    id: 'agricultural-business',
    name: 'Agricultural Business',
    slug: 'agricultural-business',
    description: 'Farmers and agricultural businesses require specialized strategies for land assets, succession planning, and commodity price management.',
    icon: 'AgricultureIcon',
    challenges: [
      'Commodity price volatility',
      'Weather and climate risks',
      'High capital requirements',
      'Intergenerational transfer complexity',
      'Cash flow seasonality'
    ],
    opportunities: [
      'Farm property tax benefits',
      'Capital gains exemption',
      'AgriInvest and AgriStability',
      'Succession planning advantages',
      'Equipment depreciation strategies'
    ],
    commonServices: [
      'Farm succession planning',
      'Risk management strategies',
      'Tax planning for farmers',
      'Estate planning with land assets',
      'Retirement income strategies'
    ],
    incomeRange: {
      low: 50000,
      high: 300000
    },
    specificNeeds: [
      'Land transfer strategies',
      'Equipment financing',
      'Crop insurance optimization',
      'Diversification planning',
      'Environmental compliance'
    ],
    careerStages: [
      {
        stage: 'Young Farmer',
        description: 'Starting or inheriting operation',
        financialPriorities: ['Land acquisition', 'Equipment investment', 'Risk management']
      },
      {
        stage: 'Growing Operation',
        description: 'Expanding acreage',
        financialPriorities: ['Scale efficiency', 'Diversification', 'Tax planning']
      },
      {
        stage: 'Established Farmer',
        description: 'Optimizing operations',
        financialPriorities: ['Succession planning', 'Wealth preservation', 'Risk mitigation']
      },
      {
        stage: 'Transition Phase',
        description: 'Passing to next generation',
        financialPriorities: ['Estate planning', 'Tax minimization', 'Retirement income']
      }
    ],
    taxConsiderations: [
      'Farm property exemptions',
      'Capital gains on farmland',
      'Farm loss restrictions',
      'Inventory valuation methods',
      'Succession tax planning'
    ],
    seoMetadata: {
      title: 'Agricultural Financial Advisor | Farm Business Wealth Management',
      description: 'Expert financial planning for Canadian farmers and agricultural businesses. Succession planning, tax strategies, and risk management for farm operations.',
      keywords: ['agricultural financial advisor', 'farm financial planning', 'farmer wealth management', 'agricultural business advisor']
    }
  },
  {
    id: 'non-profit-organizations',
    name: 'Non-Profit Organizations',
    slug: 'non-profit-organizations',
    description: 'Charities and non-profits need specialized strategies for endowment management, planned giving programs, and board governance.',
    icon: 'NonProfitIcon',
    challenges: [
      'Donor retention and growth',
      'Endowment sustainability',
      'Regulatory compliance',
      'Board financial literacy',
      'Economic downturn vulnerability'
    ],
    opportunities: [
      'Planned giving programs',
      'Endowment growth strategies',
      'Social finance initiatives',
      'Legacy gift development',
      'Investment policy optimization'
    ],
    commonServices: [
      'Endowment management',
      'Planned giving advisory',
      'Investment policy development',
      'Board governance support',
      'Risk management strategies'
    ],
    incomeRange: {
      low: 100000,
      high: 10000000
    },
    specificNeeds: [
      'Sustainable funding models',
      'Gift acceptance policies',
      'Investment governance',
      'Spending policy development',
      'Impact measurement'
    ],
    careerStages: [
      {
        stage: 'Startup Phase',
        description: 'Establishing organization',
        financialPriorities: ['Funding stability', 'Donor development', 'Compliance setup']
      },
      {
        stage: 'Growth Phase',
        description: 'Expanding programs',
        financialPriorities: ['Diversification', 'Endowment building', 'Capacity growth']
      },
      {
        stage: 'Mature Organization',
        description: 'Established presence',
        financialPriorities: ['Sustainability', 'Legacy planning', 'Innovation funding']
      },
      {
        stage: 'Legacy Phase',
        description: 'Long-term impact',
        financialPriorities: ['Perpetual funding', 'Succession planning', 'Impact optimization']
      }
    ],
    taxConsiderations: [
      'Charitable registration requirements',
      'Disbursement quota rules',
      'Unrelated business income',
      'GST/HST exemptions',
      'International giving regulations'
    ],
    seoMetadata: {
      title: 'Non-Profit Financial Advisor | Charity Investment Management',
      description: 'Specialized financial advisory for Canadian charities and non-profits. Endowment management, planned giving, and sustainable funding strategies.',
      keywords: ['non-profit financial advisor', 'charity investment management', 'endowment advisory', 'planned giving consultant']
    }
  }
];

// Helper function to get industry by slug
export const getIndustryBySlug = (slug: string): IndustryData | undefined => {
  return industries.find(industry => industry.slug === slug);
};

// Helper function to get industries by income range
export const getIndustriesByIncomeRange = (minIncome: number, maxIncome: number): IndustryData[] => {
  return industries.filter(industry => 
    industry.incomeRange.high >= minIncome && industry.incomeRange.low <= maxIncome
  );
};

// Priority combinations for Saskatchewan and Alberta
export const priorityLocationIndustryCombos = [
  // Calgary combinations
  { city: 'calgary', industry: 'medical-professionals' },
  { city: 'calgary', industry: 'small-business-owners' },
  { city: 'calgary', industry: 'corporate-executives' },
  { city: 'calgary', industry: 'real-estate-professionals' },
  
  // Edmonton combinations
  { city: 'edmonton', industry: 'medical-professionals' },
  { city: 'edmonton', industry: 'small-business-owners' },
  
  // Saskatoon combinations
  { city: 'saskatoon', industry: 'medical-professionals' },
  { city: 'saskatoon', industry: 'small-business-owners' },
  
  // Regina combinations
  { city: 'regina', industry: 'medical-professionals' },
  { city: 'regina', industry: 'small-business-owners' }
];